/**
 * Medical Aid Section Component
 *
 * 医療助成情報セクション - Medical aid form section
 * Part of the Patient Edit screen (患者編集)
 */

import React, { useEffect, useMemo, useRef } from 'react';
import {
  UseFormWatch,
  UseFormSetValue,
  FieldErrors,
  Control,
} from 'react-hook-form';

import CollapsibleCard from '@/components/molecules/CollapsibleCard';
import { type PatientFormData, type PatientFormSchemaType } from '@pages/main/patient/types';
import { usePatientMasterData } from '@/hooks/main/usePatientMasterData';
import { formatSpecialCalculation } from '@pages/main/patient/utils/medicalAidCalc';
import { MedicalAidSectionForm } from './MedicalAidSectionForm';
import { useMedicalAidHistoryMenu } from './MedicalAidHistoryMenu';

interface MedicalAidSectionProps {
  control: Control<PatientFormSchemaType>;
  watch: UseFormWatch<PatientFormSchemaType>;
  setValue: UseFormSetValue<PatientFormSchemaType>;
  errors: FieldErrors<PatientFormSchemaType>;
  isEditMode: boolean;
  isCurrent: boolean;
  isExpired: boolean;
  onMedicalAidSearch: () => void;
  onMedicalAidNumberDirectSearch: (number: string) => Promise<void>;
  isMedicalAidDirectSearchLoading?: boolean;
  setFocus?: (name: any) => void;
  patientData?: PatientFormData | null;
  onHistorySwitch?: (
    section: 'patient_information' | 'health_insurance_card' | 'medical_aid',
    historyId: number
  ) => void;
  onHistoryDelete?: (
    section: 'patient_information' | 'health_insurance_card' | 'medical_aid',
    historyId: number
  ) => void;
  /** A2: 履歴作成取消 — `/patients/edit/{id}` reset (shared with 受診者・保険証). */
  onMedicalAidHistoryCreateCancel?: () => void | Promise<void>;
  /** A2: `medical_aid_id` query present and empty = new 医療助成履歴 draft. */
  medicalAidHistoryDraftByUrl?: boolean;
  disabled?: boolean;
}

const MedicalAidSection: React.FC<MedicalAidSectionProps> = ({
  control,
  watch,
  setValue,
  isEditMode,
  isCurrent,
  isExpired,
  onMedicalAidSearch,
  onMedicalAidNumberDirectSearch,
  isMedicalAidDirectSearchLoading = false,
  patientData,
  onHistorySwitch,
  onHistoryDelete,
  onMedicalAidHistoryCreateCancel,
  medicalAidHistoryDraftByUrl = false,
  disabled = false,
}) => {
  const { data: masterData, isLoading } = usePatientMasterData();
  const insurerTypeId = watch('medical_aid.insurer_type_id');
  const isNoMedicalAid = insurerTypeId === 0; // なし
  const calcType = watch('medical_aid.calc_type');
  const limitAmount = watch('medical_aid.limit_amount');
  const limitNumMonth = watch('medical_aid.limit_num_month');
  const limitAmountMonth = watch('medical_aid.limit_amount_month');
  const claimTypeId = watch('medical_aid.claim_type_id');
  const isReimbursementMedicalAid = claimTypeId === 3;

  /**
   * A2 No86_2: 一部助成 label suffix. Built from calc_type + limit_* (same logic as
   * edit.blade.php culum_control("calc_type")). Empty when no specialCalculationCost.
   */
  const calcTypeString = useMemo(
    () => formatSpecialCalculation(calcType, limitAmount, limitNumMonth, limitAmountMonth),
    [calcType, limitAmount, limitNumMonth, limitAmountMonth]
  );

  /** A2: culum_control("calc_type") unconditionally drives 一部助成_chk from calc_type != ''. */
  const isPartialAidChecked = calcType != null;

  /** RA2/A2: 種類 includes 「無し」 (id=0). Label is 無し; master may return なし for the same id. */
  const medicalAidInsurerTypeOptions = useMemo(() => {
    // Filter out master's "None" if it exists (usually id=1) to avoid duplicates with our legacy id=0
    const filteredMaster = (masterData?.insurer_types ?? []).filter(
      t => t.id !== 1 || (t.name !== 'なし' && t.name !== '無し')
    );
    const types = [...filteredMaster];
    if (!types.some(t => t.id === 0)) {
      types.unshift({ id: 0, name: '無し', is_health_insurance: false });
    }
    return types.map(type => ({
      value: type.id,
      label: type.id === 0 ? '無し' : type.name,
    }));
  }, [masterData?.insurer_types]);

  // Handle clearing fields when medical aid type changes
  // When "なし" is selected, disable and clear fields 13.2 (medical_aid_number) and 13.4 (medical_aid_patient_number)
  // When other types are selected, clear these fields to force user re-entry
  useEffect(() => {
    if (isNoMedicalAid) {
      // "なし" selected - clear all related fields
      setValue('medical_aid.medical_aid_number', '');
      setValue('medical_aid.medical_aid_patient_number', '');
      setValue('medical_aid.insurer_cost_rate', '');
      setValue('medical_aid.cost_rate', '');
      setValue('medical_aid.family_cost_rate', '');
      setValue('medical_aid.application_start_date', '');
      setValue('medical_aid.application_end_date', '');
      setValue('medical_aid.is_partial_aid', false);
      setValue('medical_aid.calc_type', null);
      setValue('medical_aid.limit_amount', null);
      setValue('medical_aid.limit_num_month', null);
      setValue('medical_aid.limit_amount_month', null);
      setValue('medical_aid.claim_type_id', null);
    }
  }, [isNoMedicalAid, setValue]);

  /**
   * A2 parity: when 続柄 (relationship_id) changes, re-derive 医療助成負担 from the stored
   * insurer rates. Mirrors edit.blade culum_control("insurer_cost_rate") — 1/3 (本人/不明) →
   * cost_rate, 2 (家族) → family_cost_rate. Skip initial render to preserve loaded value.
   */
  const relationshipId = watch('health_insurance_card.relationship_id');
  const rawCostRate = watch('medical_aid.cost_rate');
  const rawFamilyCostRate = watch('medical_aid.family_cost_rate');
  const didSkipRelationshipMount = useRef(false);
  useEffect(() => {
    if (!didSkipRelationshipMount.current) {
      didSkipRelationshipMount.current = true;
      return;
    }
    if (isNoMedicalAid) return;
    // A2 fires culum_control only on explicit 続柄 change — skip when relationship is
    // unset (null/undefined) so a freshly-loaded insurer_cost_rate isn't overwritten
    // before the user interacts with 続柄.
    if (relationshipId !== 1 && relationshipId !== 2 && relationshipId !== 3) {
      return;
    }
    const next = relationshipId === 2 ? (rawFamilyCostRate ?? '') : (rawCostRate ?? '');
    setValue('medical_aid.insurer_cost_rate', next);
  }, [relationshipId, rawCostRate, rawFamilyCostRate, isNoMedicalAid, setValue]);

  const { historyMenu, showMedicalAidHistoryCreateCancel } = useMedicalAidHistoryMenu({
    isEditMode,
    isCurrent,
    disabled,
    patientData,
    watchedMedicalAidId: watch('medical_aid.id'),
    medicalAidHistoryDraftByUrl,
    onHistorySwitch,
    onHistoryDelete,
    onMedicalAidHistoryCreateCancel,
  });

  return (
    <CollapsibleCard
      title={`医療助成${!isCurrent && isEditMode ? '（履歴表示）' : ''}`}
      extraHeaderContent={historyMenu}
      headerBackgroundColor={
        showMedicalAidHistoryCreateCancel
          ? '#000'
          : !isCurrent && isEditMode
            ? '#6b7280'
            : undefined
      }
    >
      {/* Helper to block inputs when disabled prop is true (history creation for other sections) */}
      <div style={disabled ? { pointerEvents: 'none', opacity: 0.6 } : {}}>
        <MedicalAidSectionForm
          control={control}
          setValue={setValue}
          isLoading={isLoading}
          isExpired={isExpired}
          insurerTypeId={insurerTypeId}
          isNoMedicalAid={isNoMedicalAid}
          isReimbursementMedicalAid={isReimbursementMedicalAid}
          isPartialAidChecked={isPartialAidChecked}
          calcTypeString={calcTypeString}
          medicalAidInsurerTypeOptions={medicalAidInsurerTypeOptions}
          onMedicalAidSearch={onMedicalAidSearch}
          onMedicalAidNumberDirectSearch={onMedicalAidNumberDirectSearch}
          isMedicalAidDirectSearchLoading={isMedicalAidDirectSearchLoading}
        />
      </div>
    </CollapsibleCard>
  );
};

export default MedicalAidSection;
